import { includes, isNull, isUndefined, last, split, upperCase } from 'lodash-es'
import { Grid, Menu, Typography } from 'antd'
import type { MenuProps } from 'antd'
import {
  Link,
  Outlet,
  useLocation,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { useQuery } from 'urql'
import { graphql } from 'gql.tada'

import { Loader } from '@components/common/Loader'
import { ListButton } from '@components/common/crudButtons'
import { usePermissions } from '@/hooks/auth/usePermissions'
import { AccountType, ProjectStep } from 'src/utils/enums'
import { isProjectHubRole } from 'src/utils/projectHubRole'
import { synthesisStepPath } from 'src/routes/projects/edit/[id]/synthesis/synthesisSteps'

const { Title } = Typography

const ProjectMenuDocument = graphql(`
  query ProjectMenu($id: Int!) {
    projects_by_pk(id: $id) {
      project_type
      step
      primary_signatory_client {
        last_name
      }
      urban_analysis {
        id
        status
      }
    }
  }
`)

export type ProjectMenuOutletContext = {
  /**
   * Force-refetch the outer project menu's own urql query. The urql client
   * has no cache exchange, so a query only re-runs via its own `reexecute`
   * handle. Synthesis actions (e.g. requesting an urban analysis) mutate the
   * project but live in a *separate* query; without this the nav menu (e.g.
   * the conditional "Étude urbaine" link) stays stale until a full reload.
   */
  refreshProjectMenu: () => void
}

export const useProjectMenuOutletContext = ():
  | ProjectMenuOutletContext
  | undefined => useOutletContext<ProjectMenuOutletContext | undefined>()

export const ProjectMenu: React.FC = () => {
  const pathname = useLocation().pathname
  const breakpoint = Grid.useBreakpoint()
  const { data: accountType } = usePermissions<string>()
  const isMobile = typeof breakpoint.lg === 'undefined' ? false : !breakpoint.lg
  const { id: routeId } = useParams<{ id: string }>()
  const projectID = Number(routeId)
  // SALESMAN + SOCIETY use the project hub's own chrome (no architect menu).
  const isSalesman = isProjectHubRole(accountType)
  const [isMounted, setIsMounted] = useState(false)
  const [{ data, fetching }, reexecuteMenuQuery] = useQuery({
    query: ProjectMenuDocument,
    variables: { id: projectID },
    // Salesmen use their own project hub chrome — the architect project
    // menu is hidden for them, and the SALESMAN role can't select
    // `urban_analysis` (excluded from the salesman projection), so running
    // this query as a salesman would error. Pause it entirely.
    pause: !projectID || isSalesman,
  })
  const project = data?.projects_by_pk ?? undefined

  const refreshProjectMenu = useCallback(
    () => reexecuteMenuQuery({ requestPolicy: 'network-only' }),
    [reexecuteMenuQuery],
  )

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const selectedKeys = useMemo(() => {
    const defaultKey = 'synthesis'
    const endPath = last(split(pathname, '/'))
    if (isUndefined(endPath) || isNull(pathname)) {
      return [defaultKey]
    }
    if (pathname.match('.*/synthesis/.*')) {
      return ['synthesis']
    }

    if (pathname.match('.*/documents/.*')) {
      return ['documents']
    }
    return [endPath]
  }, [pathname])

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: NonNullable<MenuProps['items']> = [
      {
        key: 'synthesis',
        label: (
          // Link straight to the resolved step sub-page (…/synthesis/<idx>)
          // rather than the bare …/synthesis index. The index route only
          // redirects once its outlet context resolves, which races on
          // client-side navigation and can strand the user on a blank page.
          // synthesisStepPath falls back to the bare index when step is not
          // yet loaded (the index then resolves it from fresh context).
          <Link
            to={synthesisStepPath(projectID, project?.step)}
            data-cy="menu-link--synthesis"
          >
            Synthèse
          </Link>
        ),
      },
      {
        key: 'declarers',
        label: (
          <Link to={`/projects/edit/${projectID}/declarers`} data-cy="menu-link--declarers">
            Déclarants
          </Link>
        ),
      },
      {
        key: 'land',
        label: (
          <Link to={`/projects/edit/${projectID}/land`} data-cy="menu-link--land">
            Terrain
          </Link>
        ),
      },
      {
        key: 'existing-building',
        label: (
          <Link
            to={`/projects/edit/${projectID}/existing-building`}
            data-cy="menu-link--existing-building"
          >
            Hâbitation
          </Link>
        ),
      },
    ]

    if (project?.project_type == 'VERANDA') {
      items.push({
        key: 'veranda',
        label: (
          <Link to={`/projects/edit/${projectID}/veranda`} data-cy="menu-link--veranda">
            Véranda
          </Link>
        ),
      })
    }

    if (project?.project_type == 'PERGOLA') {
      items.push({
        key: 'pergola',
        label: (
          <Link to={`/projects/edit/${projectID}/pergola`} data-cy="menu-link--pergola">
            Pergola
          </Link>
        ),
      })
    }

    if (project?.project_type == 'POOL') {
      items.push({
        key: 'pool',
        label: (
          <Link to={`/projects/edit/${projectID}/pool`} data-cy="menu-link--pool">
            Piscine
          </Link>
        ),
      })
    }

    if (
      !isUndefined(project?.urban_analysis?.id) &&
      (([AccountType.ARCHITECT, AccountType.COLLABORATOR] as AccountType[]).includes(
        accountType as AccountType,
      ) ||
        project?.urban_analysis?.status == 'VALIDATED')
    ) {
      items.push({
        key: 'urban-analysis',
        label: (
          <Link
            to={`/urban-analysis/edit/${project?.urban_analysis?.id}`}
            data-cy="menu-link--urban-analysis"
          >
            Étude urbaine
          </Link>
        ),
      })
    }

    if (
      ([AccountType.ARCHITECT, AccountType.COLLABORATOR] as AccountType[]).includes(
        accountType as AccountType,
      ) &&
      includes(
        [
          ProjectStep.PROJECT_IN_PROGRESS,
          ProjectStep.PROJECT_FILE_COMPLETED,
          ProjectStep.PROJECT_FILE_SENT,
          ProjectStep.PROJECT_FILE_SUBMITTED,
          ProjectStep.PROJECT_FILE_REFUSED,
          ProjectStep.PROJECT_COMPLETED,
        ],
        project?.step,
      )
    ) {
      items.push(
        {
          key: 'cerfa',
          label: (
            <Link to={`/projects/edit/${projectID}/cerfa`} data-cy="menu-link--cerfa">
              Cerfa
            </Link>
          ),
        },
        {
          key: 'matrix',
          label: (
            <Link to={`/projects/edit/${projectID}/matrix`} data-cy="menu-link--matrix">
              Matrice
            </Link>
          ),
        },
      )
    }

    items.push({
      key: 'documents',
      label: (
        <Link to={`/projects/edit/${projectID}/documents`} data-cy="menu-link--documents">
          Documents
        </Link>
      ),
    })

    if (([AccountType.ARCHITECT] as AccountType[]).includes(accountType as AccountType)) {
      items.push({
        key: 'facturation',
        label: (
          <Link to={`/projects/edit/${projectID}/invoices`} data-cy="menu-link--invoices">
            Facturation
          </Link>
        ),
      })
    }

    if (
      ([AccountType.ARCHITECT, AccountType.COLLABORATOR] as AccountType[]).includes(
        accountType as AccountType,
      )
    ) {
      items.push({
        key: 'events',
        label: (
          <Link to={`/projects/edit/${projectID}/events`} data-cy="menu-link--events">
            Suivi
          </Link>
        ),
      })
    }

    return items
  }, [project, projectID, accountType])

  // Salesmen get no architect project-menu chrome — just the routed
  // content (their own project hub provides its own header/navigation).
  if (isSalesman) {
    return (
      <Outlet
        context={{ refreshProjectMenu } satisfies ProjectMenuOutletContext}
      />
    )
  }

  if (fetching && !project) {
    return <Loader />
  }

  return (
    <div key="project-container">
      {isMounted && (
        <>
          {isMobile ? (
            <div style={{ textAlign: 'right' }}>
              <ListButton
                resource={'projects'}
                style={{ marginTop: '0.5em', marginBottom: '0.5em' }}
              />
            </div>
          ) : (
            <div style={{ float: 'left', marginTop: '8px' }}>
              <ListButton resource={'projects'} />
            </div>
          )}
        </>
      )}
      <Title id="project-title" level={2} style={{ textAlign: 'center' }}>
        {upperCase(project?.primary_signatory_client?.last_name ?? '')}
        &nbsp;-&nbsp;{project?.project_type}
      </Title>
      <Menu
        key="project-menu"
        selectedKeys={selectedKeys}
        mode={isMobile ? 'vertical' : 'horizontal'}
        style={{ marginBottom: '0.5em', justifyContent: 'center' }}
        items={menuItems}
      />
      <Outlet
        context={{ refreshProjectMenu } satisfies ProjectMenuOutletContext}
      />
    </div>
  )
}
